<template>
  <div
    class="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
    <!-- Image Viewer Modal -->
    <ImageViewer v-if="showImageViewer" :src="wallpaper?.videoUrl || currentImageUrl"
      :wallpaper-id="wallpaper?.id || ''"
      :alt="`${wallpaper?.title || 'Wallpaper'} - ${getDeviceDisplayName(selectedDevice)}`"
      @close="showImageViewer = false" />

    <!-- Loading State -->
    <LoadingState v-if="loading" />

    <!-- Error State -->
    <ErrorState v-else-if="error" :error="error" @back-to-home="$router.push('/')" />

    <!-- Wallpaper Detail Content -->
    <div v-else-if="wallpaper" class="container mx-auto px-3 py-2 sm:px-4 sm:py-3 max-w-7xl">
      <!-- Mobile Header -->
      <div class="block lg:hidden mb-4">
        <div class="flex items-center justify-between mb-3">
          <button @click="goBack"
            class="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
            <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <div class="flex items-center gap-2">
            <button @click="toggleFavorite" :class="[
              'p-2 rounded-full transition-all duration-200',
              isFavorited
                ? 'bg-red-100 text-red-500 dark:bg-red-900/30 dark:text-red-400'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
            ]">
              <svg class="w-5 h-5" :fill="isFavorited ? 'currentColor' : 'none'" stroke="currentColor"
                viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </button>
            <button @click="shareWallpaper"
              class="p-2 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
            </button>

          </div>
        </div>
        <div class="text-center">
          <h1 class="text-xl font-bold text-gray-900 dark:text-white mb-1">{{ wallpaper.title }}</h1>
          <div class="flex items-center justify-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <span class="flex items-center gap-1">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              {{ categoryName }}
            </span>
            <span class="flex items-center gap-1">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {{ wallpaper.downloadCount || 0 }} downloads
            </span>
          </div>
        </div>
      </div>

      <!-- Desktop Header Component -->
      <div class="hidden lg:block">
        <WallpaperHeader 
          :title="wallpaper.title" 
          :category-name="categoryName"
          :download-count="wallpaper.downloadCount || 0" 
          :is-wallpaper-pack="!!wallpaper.isWallpaperPack"
          :is-live-wallpaper="!!(wallpaper.isVideo || wallpaper.isAnimated)"
          :show-quick-download="false"
          :is-downloading="false" 
          @back="goBack" />
      </div>

      <!-- Mobile Layout -->
      <div class="block lg:hidden">
        <!-- Mobile Preview Area (Top) -->
        <div class="mb-4">
          <DevicePreview 
            :device-type="mapToDevicePreviewType(selectedDevice)"
            :device-display-name="getDeviceDisplayName(selectedDevice)" 
            :image-url="currentImageUrl || ''"
            :wallpaper-id="wallpaper?.id || ''"
            :dimensions="currentDimensions" 
            :aspect-ratio="calculateAspectRatio(currentDimensions)"
            :has-multiple-variations="!!(wallpaper?.isWallpaperPack && selectedDevice && getDeviceVariations(wallpaper, selectedDevice).length > 1)"
            :variations="wallpaper?.isWallpaperPack && selectedDevice ? getDeviceVariations(wallpaper, selectedDevice) : []"
            :current-variation-index="currentVariationIndex"
            :total-variations="wallpaper?.isWallpaperPack && selectedDevice ? getDeviceVariations(wallpaper, selectedDevice).length : 0"
            :fit-option="selectedFitOption" 
            :is-loading="false"
            :is-live-wallpaper="wallpaper?.isVideo || wallpaper?.isAnimated"
            :is-video="wallpaper?.isVideo"
            :is-animated="wallpaper?.isAnimated"
            :video-url="wallpaper?.videoUrl"
            @fullscreen="showImageViewer = true"
            @share="shareWallpaper" 
            @preview-click="showImageViewer = true" 
            @touch-start="handleTouchStart"
            @touch-move="handleTouchMove" 
            @touch-end="handleTouchEnd"
            @select-variation="onSelectVariationMobile"
            @fit-changed="handleFitChanged" />
        </div>

        <!-- Mobile Controls (Compact) -->
        <div class="space-y-3">
          <!-- Compact Device Selector -->
          <div class="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
            <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Device</h3>
            <div class="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
              <button v-for="device in availableDevices" :key="device" @click="handleDeviceSelection(device)" :class="[
                'flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200',
                selectedDevice === device
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              ]">
                {{ getDeviceDisplayName(device) }}
              </button>
            </div>
          </div>

          <!-- Compact Variation Navigator - Hidden on mobile since variants are now shown below preview -->
          <!-- <VariationNavigator
            v-if="wallpaper.isWallpaperPack && selectedDevice && getDeviceVariations(wallpaper, selectedDevice).length > 1"
            :current-index="currentVariationIndex"
            :total-variations="getDeviceVariations(wallpaper, selectedDevice).length"
            :variations="getDeviceVariations(wallpaper, selectedDevice)" @previous="navigateToPreviousVariation"
            @next="navigateToNextVariation"
            @select-variation="(index) => handleVariationSelection(selectedDevice, index)"
            class="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm" /> -->

          <!-- Ad Banner (Mobile) -->
          <MobileAd ad-type="rectangle" class="mb-3" />

          <!-- Download Limit Indicator -->
          <DownloadLimitIndicator 
            :compact="true"
            @create-account="showAuthModal = true"
            @download-limit-reached="updateDownloadLimitInfo"
          />

          <!-- Compact Download Panel -->
          <div class="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
            <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Downloads</h3>
            <div class="grid grid-cols-2 gap-2">
              <button v-for="(resolution, index) in getAvailableResolutions().slice(0, 4)" :key="index"
                @click="downloadWallpaperWithAd(resolution)" :disabled="isDownloading || !canDownload"
                :class="[
                  'flex items-center justify-between p-2 rounded-lg transition-colors',
                  canDownload 
                    ? 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50'
                    : 'bg-gray-200 dark:bg-gray-600 cursor-not-allowed opacity-50'
                ]">
                <div class="text-left">
                  <div class="text-xs font-medium text-gray-900 dark:text-white">{{ resolution.name }}</div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">{{ resolution.size }}</div>
                </div>
                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
            </div>
          </div>

          <!-- SEO Content: Description -->
          <div v-if="wallpaper.description" class="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
            <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{{ wallpaper.description }}</p>
          </div>

          <!-- SEO Content: Tags -->
          <div v-if="wallpaper.tags && wallpaper.tags.length > 0"
            class="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
            <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tags</h3>
            <div class="flex flex-wrap gap-2">
              <span v-for="tag in wallpaper.tags" :key="tag"
                class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                #{{ tag }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop Layout -->
      <div class="hidden lg:grid lg:grid-cols-4 gap-4">
        <!-- Left Column - Interactive Controls -->
        <div class="lg:col-span-1 space-y-3">
          <!-- Device Selector Component -->
          <DeviceSelector :available-devices="availableDevices" :selected-device="selectedDevice"
            :device-info="deviceInfo" :is-wallpaper-pack="wallpaper.isWallpaperPack"
            @device-selected="handleDeviceSelection" />

          <!-- Variation Navigator Component -->
          <VariationNavigator
            v-if="wallpaper.isWallpaperPack && selectedDevice && getDeviceVariations(wallpaper, selectedDevice).length > 1"
            :current-index="currentVariationIndex"
            :total-variations="getDeviceVariations(wallpaper, selectedDevice).length"
            :variations="getDeviceVariations(wallpaper, selectedDevice)" @previous="navigateToPreviousVariation"
            @next="navigateToNextVariation"
            @select-variation="onSelectVariationDesktop" />

          <!-- Ad Banner (Desktop Sidebar) -->
          <MobileAd ad-type="rectangle" />

          <!-- Download Limit Indicator -->
          <DownloadLimitIndicator 
            @create-account="showAuthModal = true"
            @download-limit-reached="updateDownloadLimitInfo"
          />

          <!-- Download Panel Component -->
          <DownloadPanel :resolutions="getAvailableResolutions().slice(0, 4)" :is-downloading="isDownloading"
            :can-download="canDownload" @download="downloadWallpaperWithAd" />

          <!-- Ad Rectangle in sidebar -->
          <MobileAd ad-type="rectangle" class="mt-4" />
        </div>

        <!-- Main Preview Area -->
        <div class="lg:col-span-3">
          <DevicePreview :device-type="mapToDevicePreviewType(selectedDevice)"
            :device-display-name="getDeviceDisplayName(selectedDevice)" :image-url="currentImageUrl"
            :wallpaper-id="wallpaper?.id || ''"
            :dimensions="currentDimensions" :aspect-ratio="calculateAspectRatio(currentDimensions)"
            :has-multiple-variations="wallpaper.isWallpaperPack && selectedDevice && getDeviceVariations(wallpaper, selectedDevice).length > 1"
            :variations="wallpaper.isWallpaperPack && selectedDevice ? getDeviceVariations(wallpaper, selectedDevice) : []"
            :current-variation-index="currentVariationIndex"
            :total-variations="wallpaper.isWallpaperPack && selectedDevice ? getDeviceVariations(wallpaper, selectedDevice).length : 0"
            :fit-option="selectedFitOption" :is-loading="false"
            :is-live-wallpaper="wallpaper?.isVideo || wallpaper?.isAnimated"
            :is-video="wallpaper?.isVideo"
            :is-animated="wallpaper?.isAnimated"
            :video-url="wallpaper?.videoUrl"
            @fullscreen="showImageViewer = true"
            @share="shareWallpaper" @preview-click="showImageViewer = true" @touch-start="handleTouchStart"
            @touch-move="handleTouchMove" @touch-end="handleTouchEnd"
            @select-variation="onSelectVariationDesktop"
            @fit-changed="handleFitChanged" />

          <!-- SEO Content: Description (Desktop) - Moved below preview -->
          <div v-if="wallpaper.description" class="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm mt-4">
            <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Description</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{{ wallpaper.description }}</p>
          </div>

          <!-- SEO Content: Tags (Desktop) - Moved below preview -->
          <div v-if="wallpaper.tags && wallpaper.tags.length > 0"
            class="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm mt-4">
            <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Tags</h3>
            <div class="flex flex-wrap gap-2">
              <span v-for="tag in wallpaper.tags" :key="tag"
                class="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors cursor-pointer">
                #{{ tag }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Download Notification -->
    <DownloadNotification :show="notification.show" :type="notification.type" :message="notification.message"
      :resolution="notification.resolution" @close="hideNotification" />

    <!-- Interstitial Ad Modal -->
    <InterstitialAdModal
      :is-visible="showInterstitialAd"
      :download-info="pendingDownload"
      @download="handleInterstitialDownload"
      @close="showInterstitialAd = false"
      @skip="handleInterstitialSkip"
    />

    <!-- Mobile Interstitial Ad Handler -->
    <MobileInterstitialAd 
      ref="mobileInterstitialRef"
      trigger="download" 
      :frequency="2" 
    />

    <!-- Auth Modal -->
    <AuthModal 
      :is-open="showAuthModal" 
      @close="showAuthModal = false" 
      @success="handleAuthSuccess" 
    />
  </div>
</template>

<script setup lang="ts">
// Components
import ImageViewer from '../components/ImageViewer.vue';
import DownloadNotification from '../components/wallpaper/DownloadNotification.vue';
import LoadingState from '../components/common/LoadingState.vue';
import ErrorState from '../components/common/ErrorState.vue';

// New Modular Components
import WallpaperHeader from '../components/wallpaper-detail/WallpaperHeader.vue';
import DeviceSelector from '../components/wallpaper-detail/DeviceSelector.vue';
import VariationNavigator from '../components/wallpaper-detail/VariationNavigator.vue';
import DownloadPanel from '../components/wallpaper-detail/DownloadPanel.vue';
import DevicePreview from '../components/wallpaper-detail/DevicePreview.vue';
import DownloadLimitIndicator from '../components/ui/DownloadLimitIndicator.vue';
import AuthModal from '../components/AuthModal.vue';
import MobileAd from '../components/MobileAd.vue';
import MobileInterstitialAd from '../components/MobileInterstitialAd.vue';
import InterstitialAdModal from '../components/InterstitialAdModal.vue';
import MobileUtils from '../utils/mobileUtils';


// Vue imports
import { ref, onMounted, computed, watch } from 'vue';

// Composables
import { useWallpaperDetail } from '../composables/useWallpaperDetail';

// Services
import { centralizedImageSecurity } from '@/services/centralizedImageSecurity';

// Use the wallpaper detail composable
const {
  // State
  wallpaper,
  loading,
  error,
  isDownloading,
  showImageViewer,
  selectedDevice,
  notification,
  isFavorited,

  // Computed
  categoryName,
  availableDevices,
  deviceInfo,
  currentImageUrl,
  currentDimensions,
  currentVariationIndex,

  // Methods
  getDeviceDisplayName,
  getDeviceVariations,
  calculateAspectRatio,
  mapToDevicePreviewType,
  navigateToPreviousVariation,
  navigateToNextVariation,
  handleVariationSelection,
  handleDeviceSelection,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  getAvailableResolutions,
  downloadWallpaper,
  shareWallpaper,
  toggleFavorite,
  hideNotification,
  goBack
} = useWallpaperDetail();

// Wallpaper Fit Options State (always default to 'fill')
const selectedFitOption = ref('fill');

// Download limit state
const downloadLimitInfo = ref({
  remaining: 3,
  total: 3,
  isAuthenticated: false
});

// Auth modal state
const showAuthModal = ref(false);

// Interstitial ad state
const showInterstitialAd = ref(false);
const pendingDownload = ref<{name: string; size: string} | null>(null);
const originalDownloadFunction = ref<(() => void) | null>(null);

// Computed properties for download functionality
const canDownload = computed(() => {
  const info = centralizedImageSecurity.getRemainingDownloads();
  const canDownloadResult = info.isAuthenticated || info.remaining > 0;
  
  // Log download state for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log('[WallpaperDetail] Download state check:', {
      isAuthenticated: info.isAuthenticated,
      remaining: info.remaining,
      total: info.total,
      canDownload: canDownloadResult
    });
  }
  
  return canDownloadResult;
});



// Update download limit info
const updateDownloadLimitInfo = () => {
  downloadLimitInfo.value = centralizedImageSecurity.getRemainingDownloads();
};

// Handle successful authentication
const handleAuthSuccess = (user: { uid: string; email?: string | null }) => {
  showAuthModal.value = false;
  updateDownloadLimitInfo();
  console.log('Authentication successful:', user);
};

// Wallpaper Fit Options Methods
const handleFitChanged = (fitOption: string) => {
  // Always force Fill so the preview consistently fills the frame
  selectedFitOption.value = 'fill';
  localStorage.setItem('wallpaper-fit-preference', 'fill');
  console.log('Fit option forced to: fill (requested:', fitOption, ')');
};

// Mobile interstitial ad reference
const mobileInterstitialRef = ref<InstanceType<typeof MobileInterstitialAd> | null>(null);
type DownloadResolution = Parameters<typeof downloadWallpaper>[0];

// Override downloadWallpaper to show interstitial ad
const downloadWallpaperWithAd = async (resolution: DownloadResolution) => {
  // Add haptic feedback for mobile
  if (MobileUtils.isNative()) {
    await MobileUtils.hapticFeedback('light');
  }

  // Try to show mobile interstitial ad first
  if (MobileUtils.isNative() && mobileInterstitialRef.value) {
    mobileInterstitialRef.value.incrementTrigger();
    
    if (mobileInterstitialRef.value.canShowAd) {
      const adShown = await mobileInterstitialRef.value.showInterstitialAd();
      if (adShown) {
        // Ad was shown, proceed with download after ad closes
          setTimeout(() => downloadWallpaper(resolution), 1000);
        return;
      }
    }
  }

  // Fallback to web interstitial modal for web or if mobile ad fails
  if (!MobileUtils.isNative()) {
    // Store the original download function
    originalDownloadFunction.value = () => downloadWallpaper(resolution);
    
    // Set pending download info
    pendingDownload.value = {
      name: `${wallpaper.value?.title || 'Wallpaper'} - ${resolution.name}`,
      size: resolution.size
    };
    
    // Show interstitial ad modal
    showInterstitialAd.value = true;
  } else {
    // Direct download for mobile if no ad shown
    downloadWallpaper(resolution);
  }
};

// Interstitial Ad Methods
const handleInterstitialDownload = () => {
  if (originalDownloadFunction.value) {
    originalDownloadFunction.value();
    originalDownloadFunction.value = null;
  }
  showInterstitialAd.value = false;
  pendingDownload.value = null;
};

const handleInterstitialSkip = () => {
  if (originalDownloadFunction.value) {
    originalDownloadFunction.value();
    originalDownloadFunction.value = null;
  }
  showInterstitialAd.value = false;
  pendingDownload.value = null;
};



// Initialize fit option as Fill on load
onMounted(() => {
  selectedFitOption.value = 'fill';
  localStorage.setItem('wallpaper-fit-preference', 'fill');
});

// If wallpaper loads later and preference was 'fill', switch to 'fit' for packs to avoid stretching
watch(() => wallpaper.value?.isWallpaperPack, (isPack) => {
  if (isPack && selectedFitOption.value !== 'fill') {
    selectedFitOption.value = 'fill';
  }
});

// Avoid implicit any in template expressions by defining typed handlers
const onSelectVariationMobile = (index: number) => {
  if (selectedDevice.value) {
    handleVariationSelection(selectedDevice.value, index)
  }
}
const onSelectVariationDesktop = (index: number) => {
  if (selectedDevice.value) {
    handleVariationSelection(selectedDevice.value, index)
  }
}
</script>